import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Button, Center, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { navigate } from "wouter/use-browser-location";
import { type ReactElement } from "react";

// Error message structure from server
interface ServerErrorResponse {
  data: {
    message: string;
    code: string;
  };
}

export function IsErrorResponse(error: unknown): error is ServerErrorResponse {
  if (typeof error !== "object" || error == null || !("data" in error))
    return false;
  const data = error.data;
  return (
    typeof data === "object" &&
    data != null &&
    "message" in data &&
    typeof data.message === "string" &&
    "code" in data &&
    typeof data.code === "string"
  );
}

export function isFetchBaseQueryError(
  error: unknown,
): error is FetchBaseQueryError {
  return typeof error === "object" && error != null && "status" in error;
}

const getErrorMessageFromServer = (err: unknown): string => {
  if (IsErrorResponse(err)) {
    return err.data.message;
  }
  if (isFetchBaseQueryError(err)) {
    return "error" in err ? err.error : JSON.stringify(err.data);
  }
  return JSON.stringify(err);
};

export const displayServerErrorMessage = (err: unknown) => {
  displayErrorMessage(getErrorMessageFromServer(err));
};

export const displayErrorMessage = (message: string) => {
  openErrorModal({ message });
};

export const displayInfoMessage = (message: string) => {
  openErrorModal({ message });
};

export const displayContinueMessage = (message: ReactElement) => {
  openContinueModal({ message });
};

interface ErrorModalArgs {
  title?: string;
  message: string;
}

const openErrorModal = (args: ErrorModalArgs) => {
  const title = args.title;
  const message = args.message;
  modals.open({
    title,
    withCloseButton: false,
    children: (
      <>
        <Text size="sm">{message}</Text>{" "}
        <Center>
          <Button onClick={() => modals.closeAll()} mt="md">
            Close
          </Button>
        </Center>
      </>
    ),
  });
};

interface ContinueModalArgs {
  title?: string;
  message: ReactElement;
}

const openContinueModal = (args: ContinueModalArgs) => {
  const title = args.title;
  const message = args.message;
  modals.open({
    size: "lg",
    title,
    withCloseButton: false,
    children: (
      <>
        {message}
        <Center>
          <Button onClick={() => modals.closeAll()} mt="md">
            Continue
          </Button>
        </Center>
      </>
    ),
  });
};

export const handleFetchingError = (err: unknown) => {
  // The timeout lets us call this while rendering a component.
  setTimeout(() => {
    navigate("/error", { state: getErrorMessageFromServer(err) });
  }, 0);
};
